import type { DeepReadonly } from 'vue';
import { MatrixColumnType, type FormField, type MatrixColumn, type MatrixTableRow } from '@/models';
import { assignColumnValues } from '@/models/matrixAnswer';
import { cloneDeep } from 'lodash';

/**
 * Whether a column the form disables locks its cells: a real lock, drawn `disabled` with its reason in the column's
 * header (ADR 0011). Only a text and a plain number cell have ever read `disable`.
 */
export const isLockedColumn = (column: DeepReadonly<Pick<MatrixColumn, 'type' | 'disable' | 'options'>>) =>
  !!column.disable && (column.type === MatrixColumnType.TEXT || (column.type === MatrixColumnType.NUMBER && !column.options?.range));

export const getInitialMatrixData = (localField: FormField) => {
  const matrixRows: MatrixTableRow[] = [];

  for (const row of localField.options.rows!) {
    const rowConfig: MatrixTableRow = {
      rowId: `${row.id}`,
      rowHeader: row.label,
      dynamicRowConfig: row.dynamicRowConfig,

      columnValues: cloneDeep(localField.options.columns)
    };

    rowConfig.columnValues!.forEach(column => {
      assignColumnValues(rowConfig, row, column, localField.matrixValues!);
    });

    matrixRows.push(rowConfig);

    if (rowConfig.dynamicRows) {
      rowConfig.dynamicRows.forEach(dynamicRowId => {
        let dynamicRow;
        for (const data of localField.matrixValues!) {
          if (data.rowId == dynamicRowId) {
            dynamicRow = data;
            break;
          }
        }

        if (dynamicRow) {
          const subRow: MatrixTableRow = {
            rowId: dynamicRow.rowId,
            rowHeader: rowConfig.rowHeader,
            enableClear: true,

            columnValues: cloneDeep((localField.options.columns))
          };

          subRow.columnValues!.forEach(column => {
            dynamicRow.id = dynamicRow.rowId;
            assignColumnValues(subRow, dynamicRow, column, localField.matrixValues!);
          });
          matrixRows.push(subRow);
        }
      });
    }
  }

  return matrixRows;
};

export const matrixDataCalculations = (columns: MatrixColumn[], matrixData: MatrixTableRow[]) => {
  columns.forEach(column => {
    if (column.dynamicRowColumnFormula) {
      const columnId = column.dynamicRowColumnFormula.columnId;
      const operator = column.dynamicRowColumnFormula.operator;

      for (const matrixTableRow of matrixData) {
        if (
          typeof matrixTableRow.rowId == 'string' &&
            matrixTableRow.rowId.includes('-')
        ) {
          // this means it's a dynamic row
          continue;
        }
        const columnValues: number[] = [];
        const value = matrixTableRow.columnValues!.find(col => col.id == columnId)?.numberValue;
        if (value) {
          columnValues.push(value);
        }

        let addValueRow: MatrixTableRow = matrixTableRow;
        if (matrixTableRow.dynamicRows) {
          matrixTableRow.dynamicRows.forEach(dynamicRowId => {
            const dynamicRow = matrixData.find(
              row => row.rowId == dynamicRowId
            );
            if (dynamicRow) {
              const dynamicRowValue = dynamicRow.columnValues!.find(col => col.id == columnId)?.numberValue;
              if (dynamicRowValue) {
                columnValues.push(dynamicRowValue);
              }
            }
          });
          addValueRow.columnValues!.find(col => col.id == column.id)!.numberValue = null;
          matrixTableRow.dynamicRows.forEach(dynamicRowId => {
            for (const data of matrixData) {
              if (data.rowId == dynamicRowId) {
                data.columnValues!.find(col => col.id == column.id)!.numberValue = null;
              }
            }
          });
          const lastlDynamicRowId =
              matrixTableRow.dynamicRows[matrixTableRow.dynamicRows.length - 1];
          addValueRow = matrixData.find(
            row => row.rowId == lastlDynamicRowId
          )!;
        }

        let finalFormulaValue = 0;
        if (operator === '+') {
          let total = 0;
          for (const i of columnValues) {
            total += i;
          }
          finalFormulaValue = total;
        }

        if (finalFormulaValue > 0) {
          addValueRow.columnValues!.find(col => col.id == column.id)!.numberValue = finalFormulaValue;
        }
      }
    }

    if (column.columnFormula) {
      const columnId = column.columnFormula.columnId;
      const destColumnId = column.columnFormula.destColumnId;
      const rowId = column.columnFormula.rowId;
      const operator = column.columnFormula.operator;
      const columnValues: number[] = [];
      for (const matrixTableRow of matrixData) {
        if (matrixTableRow.rowId == `${rowId}` && columnId == destColumnId) {
          continue;
        }
        const value = matrixTableRow.columnValues!.find(col => col.id == columnId)?.numberValue;
        if (value) {
          columnValues.push(value);
        }
      }

      let finalFormulaValue = 0;
      if (operator === '+') {
        let total = 0;
        for (const i of columnValues) {
          total += i;
        }
        finalFormulaValue = total;
      }

      const addValueRow = matrixData.find(rowData => rowData.rowId == `${rowId}`);
      if (addValueRow) {
        addValueRow.columnValues!.find(col => col.id == destColumnId)!.numberValue = finalFormulaValue;
      }

    }
  });
  return matrixData;
};
